import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { EVENTS } from "@/const";

/*
 * HEIC/HEIF photos from iPhone can't be processed by the server-side image converter
 * (libheif in ImageMagick fails with "Too many auxiliary image references" on photos with depth/HDR data),
 * so they are converted to JPEG in the browser before upload. Browsers that can't decode HEIC
 * (Chrome, Firefox) get a clear error before the file is uploaded.
 */

const HEIC_MIME_TYPES = ["image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence"];
const HEIC_NAME_REGEXP = /\.(heic|heif)$/i;
// iOS Safari refuses to draw canvases larger than 16 777 216 pixels
export const MAX_CANVAS_PIXELS = 16777216;
const JPEG_QUALITY = 0.92;

export function isHeicFile(file: Pick<File, "name" | "type">): boolean {
  return HEIC_MIME_TYPES.includes((file.type || "").toLowerCase()) || HEIC_NAME_REGEXP.test(file.name || "");
}

export function hasHeicFiles(files: FileList | File[]): boolean {
  return Array.from(files).some(isHeicFile);
}

export function getJpegFileName(name: string): string {
  return HEIC_NAME_REGEXP.test(name) ? name.replace(HEIC_NAME_REGEXP, ".jpg") : `${name}.jpg`;
}

export function fitToCanvasLimit(width: number, height: number, maxPixels = MAX_CANVAS_PIXELS) {
  if (width * height <= maxPixels) return { width, height };
  const scale = Math.sqrt(maxPixels / (width * height));
  return { width: Math.max(1, Math.floor(width * scale)), height: Math.max(1, Math.floor(height * scale)) };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Can't decode ${file.name}`));
    };
    image.src = url;
  });
}

export async function convertHeicToJpeg(file: File): Promise<File> {
  const image = await loadImage(file);
  const { width, height } = fitToCanvasLimit(image.naturalWidth, image.naturalHeight);
  if (!width || !height) throw new Error(`Can't decode ${file.name}`);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  try {
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Canvas is not supported");
    context.drawImage(image, 0, 0, width, height);
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY));
    if (!blob) throw new Error(`Can't convert ${file.name}`);
    return new File([blob], getJpegFileName(file.name), { type: "image/jpeg", lastModified: file.lastModified });
  } finally {
    // release canvas memory right away, iOS has a small total canvas memory limit
    canvas.width = 0;
    canvas.height = 0;
  }
}

export function showHeicRejectedError(rejected: File[]) {
  if (!rejected.length) return;
  emitter.emit(EVENTS.STATUS_SHOW, {
    text: $t(
      "HEIC photos can't be processed in this browser: {0}. Please save them as JPG, or upload them from Safari on iPhone or Mac.",
      [rejected.map(file => file.name).join(", ")]
    ),
  });
}

/**
 * Converts HEIC/HEIF files to JPEG one by one (to keep memory low on phones).
 * Files that can't be converted are returned in `rejected` and an error is shown to the user.
 */
export async function prepareFilesForUpload(
  files: FileList | File[],
  convert: (file: File) => Promise<File> = convertHeicToJpeg
): Promise<{ files: File[]; rejected: File[] }> {
  const result: File[] = [];
  const rejected: File[] = [];
  for (const file of Array.from(files)) {
    if (!isHeicFile(file)) {
      result.push(file);
      continue;
    }
    try {
      result.push(await convert(file));
    } catch (e) {
      rejected.push(file);
    }
  }
  showHeicRejectedError(rejected);
  return { files: result, rejected };
}
